import * as pdfjsLib from 'pdfjs-dist'
import { decodePDFRawStream, PDFArray, PDFDocument, PDFName } from 'pdf-lib'
import { activeEditorExtraWidth, clamp, editResizeEpsilon, fontStack, normalizeText, pdfTextFontFamily, textHighlightBottomExtraPx, textItemId } from './utils.js'

function fontWeightFromName(name) {
  const clean = String(name || '').toLowerCase()
  if (clean.includes('bold') || clean.includes('black') || clean.includes('heavy')) return 700
  if (clean.includes('semibold') || clean.includes('demibold')) return 600
  if (clean.includes('medium')) return 500
  if (clean.includes('light')) return 300
  return 400
}

function editorExtraWidth(text, fontSize) {
  const longestLineLength = Math.max(1, ...normalizeText(text || '').split('\n').map(line => line.length))
  const perCharacterAllowance = clamp(fontSize * 0.035, 0.4, 1.5)
  return activeEditorExtraWidth + Math.min(80, longestLineLength * perCharacterAllowance)
}

function setEditorWidth(editor, width, text, fontSize) {
  const extraWidth = editorExtraWidth(text, fontSize)
  editor.dataset.extraWidth = String(extraWidth)
  editor.style.width = `calc(${width * 100}% + ${extraWidth}px)`
}

export const textEditingMethods = {
  async getTextContentCompat(page) {
    const reader = page.streamTextContent().getReader()
    const content = {
      items: [],
      styles: Object.create(null),
      lang: null,
    }

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        content.lang ??= value.lang
        Object.assign(content.styles, value.styles)
        content.items.push(...value.items)
      }
    } finally {
      reader.releaseLock()
    }

    return content
  },

  renderTextEditPreview(pageState, viewport) {
    this.ctx.save()
    this.ctx.textBaseline = 'alphabetic'

    for (const edit of Object.values(pageState.textEdits) as any[]) {
      const x = edit.x * viewport.width
      const y = edit.y * viewport.height
      const boxWidth = edit.width * viewport.width
      const boxHeight = edit.height * viewport.height
      const coverX = (edit.coverX ?? edit.x) * viewport.width
      const coverY = (edit.coverY ?? edit.y) * viewport.height
      const coverWidth = (edit.coverWidth ?? edit.width) * viewport.width
      const coverHeight = (edit.coverHeight ?? edit.height) * viewport.height
      const fontSize = Math.max(8, (edit.fontHeight || edit.height) * viewport.height)
      const fontWeight = edit.fontWeight || 400

      this.ctx.fillStyle = edit.coverColor || this.sampleTextEditCoverColor(edit) || '#fff'
      this.ctx.fillRect(Math.max(0, coverX), Math.max(0, coverY), coverWidth, coverHeight)
      this.ctx.fillStyle = edit.textColor || '#111820'
      this.ctx.font = `${fontWeight} ${fontSize}px ${fontStack(edit.fontFamily)}`
      const lineHeight = fontSize * 1.2
      const lines = this.wrapText(edit.text, boxWidth, text => this.ctx.measureText(text).width)
      lines.forEach((line, index) => {
        const baseline = y + fontSize + index * lineHeight
        if (baseline <= y + boxHeight + lineHeight * 0.25) this.ctx.fillText(line, x, baseline)
      })
    }

    this.ctx.restore()
  },

  async renderTextEditLayer(page, viewport, pageState) {
    await page.getOperatorList()
    const fontFaces = await this.ensureSourceFontFaces(pageState)
    const content = await this.getTextContentCompat(page)
    pageState.textItems = content.items
      .map((item, textIndex) => this.toEditableTextItem(item, textIndex, pageState, viewport, content.styles, page, fontFaces))
      .filter(Boolean)

    for (const item of pageState.textItems) {
      const id = textItemId(item)
      const edit = pageState.textEdits[id]
      if (textItemId(this.selectedTextItem || {}) === id) this.selectedTextItem = this.textItemWithEdit(item, edit)
      const isSelected = textItemId(this.selectedTextItem || {}) === id
      const isEditing = isSelected && this.editingTextItemId === id
      const control = document.createElement(isEditing ? 'textarea' : 'button') as HTMLTextAreaElement | HTMLButtonElement
      const displayItem = this.textItemWithEdit(item, edit)
      control.className = 'editable-text'
      if (control instanceof HTMLButtonElement) control.type = 'button'
      if (isEditing) {
        control.classList.add('in-place-editor')
        control.value = edit?.text ?? item.text
        control.setAttribute('aria-label', 'Edit PDF text')
      } else {
        control.textContent = edit?.text ?? item.text
      }
      control.dataset.textId = id
      control.setAttribute('aria-label', `${edit ? 'Edit changed text' : 'Edit text'}: ${item.text}`)
      control.style.left = `${displayItem.x * 100}%`
      control.style.top = `${displayItem.y * 100}%`
      const fontSize = Math.max((displayItem.fontHeight ?? displayItem.height) * viewport.height, 8)
      if (isEditing) setEditorWidth(control, displayItem.width, control.value, fontSize)
      else control.style.width = `${displayItem.width * 100}%`
      control.style.height = isEditing
        ? `${displayItem.height * 100}%`
        : `calc(${displayItem.height * 100}% + ${textHighlightBottomExtraPx}px)`
      control.style.fontSize = `${fontSize}px`
      control.style.fontFamily = fontStack(displayItem.fontFamily)
      control.style.fontWeight = displayItem.fontWeight || 400
      if (isEditing) {
        control.style.color = displayItem.textColor || ''
        control.style.backgroundColor = displayItem.coverColor || this.sampleTextEditCoverColor(item)
      }
      control.classList.toggle('changed', Boolean(edit))
      control.classList.toggle('selected', isSelected)
      const searchMatch = this.searchResults.some(result => result.pageId === item.pageId && result.textIndex === item.textIndex)
      const currentSearchResult = this.searchResults[this.searchResultIndex]
      control.classList.toggle('search-match', searchMatch)
      control.classList.toggle('search-current', currentSearchResult?.pageId === item.pageId && currentSearchResult?.textIndex === item.textIndex)
      control.addEventListener('click', event => {
        event.stopPropagation()
        if (!this.editTextMode || isSelected) return
        this.selectTextItem(item)
      })
      control.addEventListener('dblclick', event => {
        event.preventDefault()
        event.stopPropagation()
        if (this.editTextMode) this.startTextEdit(item)
      })
      if (isEditing) this.bindInPlaceEditor(control, item)
      this.textEditLayer.append(control)
    }
  },

  toEditableTextItem(item, textIndex, pageState, viewport, styles = {}, page = null, fontFaces = new Map()) {
    const text = item.str?.trim()
    if (!text) return null
    const fontStyle = styles[item.fontName] || {}

    const tx = pdfjsLib.Util.transform(viewport.transform, item.transform)
    const fontHeight = Math.hypot(tx[2], tx[3])
    const width = Math.max(item.width * viewport.scale, 1)
    const glyphHeight = Math.max(fontHeight, 1)
    const height = glyphHeight * 1.28
    const left = tx[4]
    const top = tx[5] - glyphHeight - (height - glyphHeight) / 2

    // Cover box hugs the font's real ascent/descent so it erases the original
    // glyphs without eating into the lines above/below. Falls back to the
    // generic 1.0em/0.28em constants when metrics aren't available.
    const { ascent, descent } = this.fontMetricsForItem(page, item.fontName)
    const coverTop = tx[5] - glyphHeight * ascent
    const coverHeight = glyphHeight * (ascent + descent)

    if (!Number.isFinite(left) || !Number.isFinite(top)) return null

    const drawInfo = this.fontDrawInfoForItem(page, item.fontName)
    const textItem: any = {
      fontName: drawInfo.baseFont,
      composite: drawInfo.composite,
      spaceWidthEm: this.estimateSpaceWidthEm(item, width, glyphHeight, drawInfo),
      sourceIndex: pageState.sourceIndex,
      documentId: pageState.documentId,
      pageId: pageState.id,
      textIndex,
      text,
      x: clamp(left / viewport.width, 0, 1),
      y: clamp(top / viewport.height, 0, 1),
      width: clamp(width / viewport.width, 0.01, 1),
      height: clamp(height / viewport.height, 0.01, 1),
      coverX: clamp(left / viewport.width, 0, 1),
      coverY: clamp(coverTop / viewport.height, 0, 1),
      coverWidth: clamp(width / viewport.width, 0.01, 1),
      coverHeight: clamp(coverHeight / viewport.height, 0.01, 1),
      fontHeight: glyphHeight / viewport.height,
      charToCid: drawInfo.charToCode,
      originalText: text,
    }
    const fontFace = fontFaces.get(textItem.fontName)
    const pdfJsFontFamily = this.pdfJsFontFamilyForItem(page, item.fontName, fontStyle)
    textItem.fontFamily = fontFace?.family || pdfJsFontFamily || pdfTextFontFamily(textItem, fontStyle)
    textItem.fontWeight = fontFace?.weight || fontWeightFromName(textItem.fontName || item.fontName || fontStyle.fontFamily)
    return {
      ...textItem,
      ...this.sampleTextItemStyle(textItem),
    }
  },

  fontDrawInfoForItem(page, fontName) {
    // Everything needed to redraw a run in its original font at export time:
    // the BaseFont name (used to find the font resource), whether it is a
    // composite/CID font (2-byte codes) or simple (1-byte), and a char -> code
    // map derived from the font's toUnicode table.
    const fallback = { baseFont: '', composite: false, charToCode: {}, widths: null }
    if (!page || !fontName) return fallback

    try {
      const font = page.commonObjs.get(fontName)
      if (!font) return fallback
      const charToCode = {}
      const map = font.toUnicode?._map || []
      map.forEach((char, code) => {
        if (char && char !== '￿' && charToCode[char] == null) charToCode[char] = code
      })
      return { baseFont: font.name || '', composite: !!font.composite, charToCode, widths: font.widths }
    } catch {
      return fallback
    }
  },

  pdfJsFontFamilyForItem(page, fontName, fontStyle: { fontSubstitutionLoadedName?: string } = {}) {
    if (!page || !fontName || !document.fonts) return ''
    try {
      const candidates = [
        fontStyle.fontSubstitutionLoadedName,
        page.commonObjs.get(fontName)?.loadedName,
      ].filter(Boolean)
      const loadedFamilies = new Set(Array.from(document.fonts)
        .filter((face: any) => face.status === 'loaded')
        .map((face: any) => face.family?.replace(/^['"]|['"]$/g, '')))
      return candidates
        .map(candidate => String(candidate).replace(/^['"]|['"]$/g, ''))
        .find(candidate => loadedFamilies.has(candidate)) || ''
    } catch {
      return ''
    }
  },

  estimateSpaceWidthEm(item, width, glyphHeight, drawInfo) {
    // Derive the font's inter-word advance (in em) from this run so word gaps
    // survive re-drawing in fonts whose space has no glyph (e.g. LaTeX text
    // fonts). Falls back to a typical 0.3em when it can't be measured.
    const source = item.str || ''
    const spaceCount = (source.match(/ /g) || []).length
    if (!spaceCount || !drawInfo.widths || !(width > 0) || !(glyphHeight > 0)) return 0.3

    let glyphEm = 0
    for (const char of source) {
      if (char === ' ') continue
      const code = drawInfo.charToCode[char]
      const advance = code != null ? drawInfo.widths[code] : null
      if (typeof advance !== 'number') return 0.3
      glyphEm += advance / 1000
    }
    const estimate = (width / glyphHeight - glyphEm) / spaceCount
    return estimate > 0.05 && estimate < 1.5 ? estimate : 0.3
  },

  fontMetricsForItem(page, fontName) {
    // Generic fallback matches the historic constants: cover from 1.0em above
    // the baseline to 0.28em below it (height 1.28em).
    const fallback = { ascent: 1, descent: 0.28 }
    if (!page || !fontName) return fallback

    try {
      const font = page.commonObjs.get(fontName)
      const ascent = Math.abs(font?.ascent)
      const descent = Math.abs(font?.descent)
      // Some fonts declare degenerate metrics; keep the safe fallback then.
      if (!(ascent > 0.3) || !Number.isFinite(descent)) return fallback
      // Pad the descender slightly so glyph tails are never clipped.
      return { ascent, descent: descent + 0.03 }
    } catch {
      return fallback
    }
  },

  async ensureSourceFontFaces(pageState) {
    const source = this.documentForPage(pageState)
    const cacheKey = `${pageState.documentId}:${pageState.sourceIndex}`
    if (!source?.sourceBytes || source.passwordProtected || !document.fonts) return new Map()
    if (this.sourceFontFaces.has(cacheKey)) return this.sourceFontFaces.get(cacheKey)

    const fontFaces = new Map()
    this.sourceFontFaces.set(cacheKey, fontFaces)

    try {
      const doc = await PDFDocument.load(source.sourceBytes.slice(0))
      const page = doc.getPage(pageState.sourceIndex)
      const resources: any = page.node.Resources()
      const fonts: any = resources && doc.context.lookup(resources.get(PDFName.of('Font')))
      if (!fonts?.keys) return fontFaces

      let fontIndex = 0
      for (const key of fonts.keys()) {
        const font: any = doc.context.lookup(fonts.get(key))
        const descendants: any = doc.context.lookup(font?.get?.(PDFName.of('DescendantFonts')))
        const descendant: any = descendants instanceof PDFArray ? doc.context.lookup(descendants.get(0)) : font
        const descriptor: any = doc.context.lookup(descendant?.get?.(PDFName.of('FontDescriptor')))
        const fontFile: any = doc.context.lookup(
          descriptor?.get?.(PDFName.of('FontFile2')) ||
          descriptor?.get?.(PDFName.of('FontFile3')) ||
          descriptor?.get?.(PDFName.of('FontFile'))
        )
        const bytes = fontFile && (fontFile.getUnencodedContents?.() || decodePDFRawStream(fontFile).decode())
        const baseFont = font?.get?.(PDFName.of('BaseFont'))?.decodeText?.() || ''

        if (!bytes?.length || !baseFont) continue
        const family = `birme-pdf-source-${pageState.documentId}-${pageState.sourceIndex}-${fontIndex++}`
        const weight = fontWeightFromName(baseFont)
        try {
          // Some PDF subset fonts lack a cmap table and the browser's font
          // sanitizer rejects them ("OTS parsing error"). Skip just that font
          // so the rest of the page's fonts still load for the preview.
          const face = new FontFace(family, bytes, { weight: String(weight) })
          await face.load()
          document.fonts.add(face)
          // Keyed by BaseFont name to match how export links runs to fonts.
          fontFaces.set(baseFont, { family, weight, face })
        } catch {
          // Leave this font unmapped; callers fall back to a generic family.
        }
      }
    } catch {
      fontFaces.clear()
    }

    return fontFaces
  },

  clearSourceFontFaces() {
    if (document.fonts) {
      for (const fontFaces of this.sourceFontFaces.values()) {
        for (const { face } of fontFaces.values()) {
          if (face) document.fonts.delete(face)
        }
      }
    }
    this.sourceFontFaces.clear()
  },

  bindInPlaceEditor(editor, item) {
    let closingEditor = false
    const historySnapshot = this.captureHistoryState()
    let historyRecorded = false
    const recordEditorHistory = () => {
      if (historyRecorded) return
      this.recordHistorySnapshot(historySnapshot)
      historyRecorded = true
    }
    const update = (recordChange = false) => {
      const pageState = this.visiblePages[this.currentIndex]
      if (!pageState || !editor.isConnected) return
      const rect = this.textEditLayer.getBoundingClientRect()
      const editorRect = editor.getBoundingClientRect()
      if (!rect.width || !rect.height || !editorRect.width || !editorRect.height) return
      const id = textItemId(item)
      const text = normalizeText(editor.value)
      const original = item.originalText || item.text
      const existing = pageState.textEdits[id]
      const currentExtraWidth = Number(editor.dataset.extraWidth) || activeEditorExtraWidth
      const width = clamp((editorRect.width - currentExtraWidth) / rect.width, 0.01, 1)
      const height = clamp(editorRect.height / rect.height, 0.01, 1)
      const manuallyResized = existing && (
        Math.abs(width - existing.width) > editResizeEpsilon ||
        Math.abs(height - existing.height) > editResizeEpsilon
      )
      if (text === original) {
        if (existing && recordChange) recordEditorHistory()
        delete pageState.textEdits[id]
        this.selectedTextItem = this.textItemWithEdit(item, null)
        setEditorWidth(editor, item.width, text, Number.parseFloat(editor.style.fontSize) || 8)
        editor.style.height = `${item.height * 100}%`
        this.updateDirtyState()
        return
      }
      const edit = this.autoSizeTextEdit(this.buildTextEdit(item, text, {
        x: clamp((editorRect.left - rect.left) / rect.width, 0, 1),
        y: clamp((editorRect.top - rect.top) / rect.height, 0, 1),
        width,
        height,
        autoWidth: manuallyResized ? false : existing?.autoWidth,
        autoHeight: manuallyResized ? false : existing?.autoHeight,
      }), item)
      if (recordChange && JSON.stringify(edit) !== JSON.stringify(existing)) recordEditorHistory()
      pageState.textEdits[id] = edit
      setEditorWidth(editor, edit.width, text, Number.parseFloat(editor.style.fontSize) || 8)
      editor.style.height = `${edit.height * 100}%`
      this.selectedTextItem = edit
      this.updateDirtyState()
    }

    editor.addEventListener('input', () => update(true))
    editor.addEventListener('keyup', () => {
      if (!closingEditor) update(true)
    })
    editor.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closingEditor = true
        update(true)
        this.exitTextEdit()
      }
    })
    editor.addEventListener('mouseup', () => update(true))
    const resizeObserver = new ResizeObserver(() => update(false))
    resizeObserver.observe(editor)
    editor.addEventListener('blur', () => {
      resizeObserver.disconnect()
      if (closingEditor) return
      update(true)
      this.editingTextItemId = null
      this.renderCurrentPage()
    })
    if (this.focusInPlaceEditor) {
      this.focusInPlaceEditor = false
      requestAnimationFrame(() => editor.focus())
    }
  },

  selectTextItem(item) {
    this.editingAnnotationId = null
    this.selectedAnnotationId = null
    this.editingTextItemId = null
    const pageState = this.visiblePages[this.currentIndex]
    const edit = pageState?.textEdits[textItemId(item)]
    this.selectedTextItem = this.textItemWithEdit(item, edit)
    document.querySelectorAll('.annotation-note.selected').forEach(note => note.classList.remove('selected'))
    document.querySelectorAll('.editable-text').forEach(control =>
      control.classList.toggle('selected', control.getAttribute('data-text-id') === textItemId(item)))
    this.syncAnnotationTools()
    this.updateDeleteControl()
    this.setStatus('Text selected. Double-click to edit its content.')
  },

  startTextEdit(item) {
    this.selectTextItem(item)
    this.editingTextItemId = textItemId(item)
    this.focusInPlaceEditor = true
    this.renderCurrentPage()
  },

  exitTextEdit() {
    if (!this.editingTextItemId) return
    clearTimeout(this.panelEditRenderTimer)
    this.editingTextItemId = null
    this.focusInPlaceEditor = false
    this.resetTextEditor()
    this.renderCurrentPage()
    this.setStatus('Text edit closed. Double-click to edit again.')
  },

  deselectPdfText() {
    if (!this.selectedTextItem) return
    this.editingTextItemId = null
    this.selectedTextItem = null
    this.focusInPlaceEditor = false
    document.querySelectorAll('.editable-text.selected').forEach(control => control.classList.remove('selected'))
    this.syncAnnotationTools()
    this.updateDeleteControl()
  },

  commitTextEdit(item, replacement) {
    const pageState = this.visiblePages[this.currentIndex]
    if (!pageState || !item) return
    const id = textItemId(item)
    const original = item.originalText || item.text
    if (replacement === original) {
      if (!pageState.textEdits[id]) return
      this.recordHistory()
      delete pageState.textEdits[id]
      this.selectedTextItem = this.textItemWithEdit(item, null)
      this.updateDirtyState()
      return
    }
    this.recordHistory()
    pageState.textEdits[id] = this.autoSizeTextEdit(this.buildTextEdit(item, replacement), item)
    this.selectedTextItem = pageState.textEdits[id]
    this.updateDirtyState()
  },

  removePdfText(item) {
    const pageState = this.visiblePages[this.currentIndex]
    if (!pageState || !item) return false

    const id = textItemId(item)
    if (pageState.textEdits[id]?.text === '') return false
    this.recordHistory()
    pageState.textEdits[id] = this.autoSizeTextEdit(this.buildTextEdit(item, ''), item)
    this.selectedTextItem = null
    this.editingTextItemId = null
    this.focusInPlaceEditor = false
    this.updateDirtyState()
    this.renderCurrentPage()
    this.setStatus('PDF text removed.')
    return true
  },

  sampleTextEditCoverColor(rect) {
    if (!rect || !this.canvas.width || !this.canvas.height) return '#ffffff'

    const x = rect.x * this.canvas.width
    const y = rect.y * this.canvas.height
    const width = rect.width * this.canvas.width
    const height = rect.height * this.canvas.height
    const rawPoints: Array<[number, number]> = []
    let steps = 7
    const inset = 1
    const outside = 3

    for (let i = 0; i < steps; i++) {
      const ratio = steps === 1 ? 0.5 : i / (steps - 1)
      rawPoints.push(
        [x + ratio * width, y - outside],
        [x + ratio * width, y + height + outside],
        [x - outside, y + ratio * height],
        [x + width + outside, y + ratio * height],
      )
    }

    rawPoints.push(
      [x + inset, y + inset],
      [x + width - inset, y + inset],
      [x + inset, y + height - inset],
      [x + width - inset, y + height - inset],
    )

    const samples: number[][] = rawPoints
      .map(([px, py]) => [
        clamp(Math.round(px), 0, this.canvas.width - 1),
        clamp(Math.round(py), 0, this.canvas.height - 1),
      ])
      .map(([px, py]) => Array.from(this.ctx.getImageData(px, py, 1, 1).data).slice(0, 3) as number[])
      .filter(sample => Math.max(...sample) - Math.min(...sample) < 18)

    const median = (channel: number) => {
      const values = samples.map(sample => sample[channel]).sort((a, b) => a - b)
      return values[Math.floor(values.length / 2)]
    }
    const toHex = (value: number) => value.toString(16).padStart(2, '0')

    if (!samples.length) return '#ffffff'
    return `#${toHex(median(0))}${toHex(median(1))}${toHex(median(2))}`
  },

  sampleTextItemStyle(rect) {
    if (!rect || !this.canvas.width || !this.canvas.height) return {}

    const canvasWidth = this.canvas.width
    const canvasHeight = this.canvas.height
    const left = clamp(Math.floor((rect.coverX ?? rect.x) * canvasWidth), 0, canvasWidth - 1)
    const top = clamp(Math.floor((rect.coverY ?? rect.y) * canvasHeight), 0, canvasHeight - 1)
    const right = clamp(Math.ceil(left + (rect.coverWidth ?? rect.width) * canvasWidth), left + 1, canvasWidth)
    const bottom = clamp(Math.ceil(top + (rect.coverHeight ?? rect.height) * canvasHeight), top + 1, canvasHeight)
    const regionWidth = right - left
    const regionHeight = bottom - top
    if (regionWidth <= 0 || regionHeight <= 0) return {}

    // One readback for the whole box; per-pixel getImageData over every text
    // item freezes the page for tens of seconds on a full page.
    const { data } = this.ctx.getImageData(left, top, regionWidth, regionHeight)
    const samples = []
    for (let offset = 0; offset < data.length; offset += 4) {
      const red = data[offset]
      const green = data[offset + 1]
      const blue = data[offset + 2]
      const value = (red + green + blue) / 3
      if (value < 245) samples.push([red, green, blue, value])
    }

    const textSamples = samples.filter(([, , , value]) => value < 180)
    const colorSamples = (textSamples.length ? textSamples : samples).sort((a, b) => a[3] - b[3])
    if (!colorSamples.length) return {}

    // Average the darkest ~10% of ink pixels. Anti-aliased edge pixels are
    // lighter than the true fill, so a mid-percentile sample reads too light.
    const darkest = colorSamples.slice(0, Math.max(1, Math.round(colorSamples.length * 0.1)))
    const channelAverage = channel => Math.round(darkest.reduce((sum, sample) => sum + sample[channel], 0) / darkest.length)
    const red = channelAverage(0)
    const green = channelAverage(1)
    const blue = channelAverage(2)

    // Almost all PDF body text is black, but low display zoom samples it as
    // dark grey, which would export visibly lighter than the original. Snap any
    // neutral (near-grey), dark-enough colour to pure black. Coloured text
    // (links, headings) and genuinely light greys keep their sampled colour.
    const isNeutral = Math.max(red, green, blue) - Math.min(red, green, blue) <= 40
    const brightness = (red + green + blue) / 3
    if (isNeutral && brightness <= 170) return { textColor: '#000000' }

    const toHex = value => value.toString(16).padStart(2, '0')
    return { textColor: `#${toHex(red)}${toHex(green)}${toHex(blue)}` }
  },

  rotateTextEditRect(rect, delta) {
    const fit = next => {
      const width = clamp(next.width, 0.01, 1)
      const height = clamp(next.height, 0.01, 1)
      return {
        ...next,
        width,
        height,
        x: clamp(next.x, 0, 1 - width),
        y: clamp(next.y, 0, 1 - height),
      }
    }
    const normalizedDelta = ((delta % 360) + 360) % 360
    const rotateBox = box => {
      if (normalizedDelta === 90) {
        return fit({
          ...box,
          x: 1 - box.y - box.height,
          y: box.x,
          width: box.height,
          height: box.width,
        })
      }
      if (normalizedDelta === 180) {
        return fit({
          ...box,
          x: 1 - box.x - box.width,
          y: 1 - box.y - box.height,
        })
      }
      if (normalizedDelta === 270) {
        return fit({
          ...box,
          x: box.y,
          y: 1 - box.x - box.width,
          width: box.height,
          height: box.width,
        })
      }
      return box
    }
    const rotateCover = next => {
      if (rect.coverX == null || rect.coverY == null || rect.coverWidth == null || rect.coverHeight == null) return next
      const cover = rotateBox({
        x: rect.coverX,
        y: rect.coverY,
        width: rect.coverWidth,
        height: rect.coverHeight,
      })
      return {
        ...next,
        coverX: cover.x,
        coverY: cover.y,
        coverWidth: cover.width,
        coverHeight: cover.height,
      }
    }
    if (normalizedDelta === 90) {
      return rotateCover(fit({
        ...rect,
        x: 1 - rect.y - rect.height,
        y: rect.x,
        width: rect.height,
        height: rect.width,
        fontHeight: rect.fontHeight,
      }))
    }
    if (normalizedDelta === 180) {
      return rotateCover(fit({
        ...rect,
        x: 1 - rect.x - rect.width,
        y: 1 - rect.y - rect.height,
      }))
    }
    if (normalizedDelta === 270) {
      return rotateCover(fit({
        ...rect,
        x: rect.y,
        y: 1 - rect.x - rect.width,
        width: rect.height,
        height: rect.width,
        fontHeight: rect.fontHeight,
      }))
    }
    return rect
  },

  buildTextEdit(item, text, overrides: any = {}) {
    const pageState = this.visiblePages[this.currentIndex]
    const existing: any = pageState?.textEdits[textItemId(item)] || {}
    const existingCoverColor = existing.coverColor?.toLowerCase()
    const coverColor = overrides.coverColor ??
      (existingCoverColor && existingCoverColor !== '#ffffff' ? existing.coverColor : this.sampleTextEditCoverColor(item))

    return {
      sourceIndex: item.sourceIndex,
      documentId: item.documentId,
      pageId: item.pageId,
      textIndex: item.textIndex,
      originalText: item.originalText || existing.originalText || item.text,
      text,
      x: overrides.x ?? existing.x ?? item.x,
      y: overrides.y ?? existing.y ?? item.y,
      width: overrides.width ?? existing.width ?? item.width,
      height: overrides.height ?? existing.height ?? item.height,
      coverX: overrides.coverX ?? existing.coverX ?? item.coverX ?? item.x,
      coverY: overrides.coverY ?? existing.coverY ?? item.coverY ?? item.y,
      coverWidth: overrides.coverWidth ?? existing.coverWidth ?? item.coverWidth ?? item.width,
      coverHeight: overrides.coverHeight ?? existing.coverHeight ?? item.coverHeight ?? item.height,
      fontHeight: overrides.fontHeight ?? existing.fontHeight ?? item.fontHeight,
      fontFamily: overrides.fontFamily ?? existing.fontFamily ?? item.fontFamily,
      fontWeight: overrides.fontWeight ?? existing.fontWeight ?? item.fontWeight,
      textColor: overrides.textColor ?? existing.textColor ?? item.textColor,
      fontName: overrides.fontName ?? existing.fontName ?? item.fontName,
      composite: overrides.composite ?? existing.composite ?? item.composite,
      spaceWidthEm: overrides.spaceWidthEm ?? existing.spaceWidthEm ?? item.spaceWidthEm,
      charToCid: overrides.charToCid ?? existing.charToCid ?? item.charToCid,
      coverColor,
      autoWidth: overrides.autoWidth ?? existing.autoWidth ?? false,
      autoHeight: overrides.autoHeight ?? existing.autoHeight ?? false,
    }
  },

  autoSizeTextEdit(edit, item) {
    const layerRect = this.textEditLayer.getBoundingClientRect()
    if (!layerRect.width || !layerRect.height) return edit

    const originalWidth = item.width || edit.width
    const originalHeight = item.height || edit.height
    const widthChanged = Math.abs(edit.width - originalWidth) > editResizeEpsilon
    const heightChanged = Math.abs(edit.height - originalHeight) > editResizeEpsilon
    const resizedTall = edit.height >= originalHeight * 2
    const shouldAutoWidth = edit.autoWidth || (!widthChanged && !heightChanged)
    const fontSize = Math.max(8, (edit.fontHeight || originalHeight) * layerRect.height)

    this.ctx.save()
    this.ctx.font = `${edit.fontWeight || 400} ${fontSize}px ${fontStack(edit.fontFamily)}`
    const measure = text => this.ctx.measureText(text).width
    const paragraphs = normalizeText(edit.text).split('\n')
    const widestLine = Math.max(1, ...paragraphs.map(line => measure(line)))

    if (shouldAutoWidth && !resizedTall) {
      const width = clamp((widestLine + 2) / layerRect.width, originalWidth, 1 - edit.x)
      this.ctx.restore()
      return {
        ...edit,
        width,
        height: originalHeight,
        autoWidth: true,
        autoHeight: false,
      }
    }

    if (resizedTall) {
      const boxWidth = Math.max(1, edit.width * layerRect.width)
      const lines = this.wrapText(edit.text, boxWidth, measure)
      const lineHeight = fontSize * 1.2
      const height = clamp((fontSize + Math.max(0, lines.length - 1) * lineHeight) / layerRect.height, edit.height, 1 - edit.y)
      this.ctx.restore()
      return {
        ...edit,
        height,
        autoWidth: false,
        autoHeight: true,
      }
    }

    this.ctx.restore()
    return edit
  },

  textItemWithEdit(item, edit) {
    return {
      ...item,
      ...(edit || {}),
      originalText: item.originalText || edit?.originalText || item.text,
      fontWeight: edit?.fontWeight ?? item.fontWeight,
      textColor: edit?.textColor ?? item.textColor,
      fontName: edit?.fontName ?? item.fontName,
      composite: edit?.composite ?? item.composite,
      spaceWidthEm: edit?.spaceWidthEm ?? item.spaceWidthEm,
      charToCid: edit?.charToCid ?? item.charToCid,
    }
  },

  resetTextEditor() {
    this.focusInPlaceEditor = false
  },
}
